/**
 * Calendar appointments over MCP — the READ half. Writes live in
 * `calendarWrites.js`, deletion in `calendarDeletes.js`, beside this file.
 *
 * ## Why this does NOT call `calendarStore.listEvents()`
 *
 * `listEvents()` lists `['personal', ...teamCalendarKeys()]` and swallows every
 * per-scope failure into `console.warn` + `[]`. Two silences stack:
 *
 * - `teamCalendarKeys()` reads `useTeamsStore.getState().teams`, and the boot
 *   fetch (`useWorkspaceBootstrap`) is fire-and-forget and sets `[]` on
 *   failure — so a request landing in the first seconds after launch sees only
 *   the personal calendar with no error at all;
 * - even a team whose scope IS known contributes zero rows when its own list
 *   throws.
 *
 * So a short calendar is indistinguishable from an empty one, and
 * `scopesConsulted` computed from `resolveScopes` alone would be a LIE — it
 * would name a scope that returned nothing because it failed. This module
 * therefore lists **each scope itself** and moves a scope from `consulted` to
 * `unavailable` when *its* list throws. That is a real difference from the
 * boards read path and it is not a shortcut anyone should take back out.
 *
 * (The app's own UI still has the short-calendar problem. That is a
 * pre-existing, user-visible gap and is not fixed here.)
 *
 * ## Occurrences, not masters
 *
 * `appointments_list` returns EXPANDED OCCURRENCES through the app's own
 * `expandEvents`, so exdate suppression, override substitution, the
 * malformed-rule fallback and the 1000-step cap are the identical code the
 * grid renders from. A second expansion implementation is the "two
 * implementations that drift" failure this codebase keeps paying for.
 *
 * ## What is NOT here, and is not an omission
 *
 * Pending invitations are UI ghosts built in `CalendarView` from
 * `useCalendarInvitesStore` with synthetic `invite:<id>` uids — they are not
 * records, `listEvents()` cannot see them, and neither can this. The tool
 * description says so. Invitations themselves go through the app's
 * authenticated API client, which is pinned as unreachable from this module
 * graph, and are out of this phase by decision.
 */
import { expandEvents } from '../../utils/calendarRecurrence';
import { eventInScope } from '../../extensions/bundled/calendar/calendarStore';
import { fromDateKey, addDays, startOfDay, toDateKey, eventRange } from '../../utils/calendarDates';
import { resolveScopes } from '../scopes';
import { mcpError } from '../errors';
import {
  MAX_WINDOW_DAYS,
  checkDateKey,
  eventOf,
  hostTimezone,
  listCalendarRows,
  projectOccurrence,
  revsForCalendar,
  scopeKeyForCalendar,
} from '../calendarRecords';

const DEFAULT_LIMIT = 100;
const MAX_LIMIT = 200;
const DEFAULT_OCCURRENCES = 10;
const MAX_OCCURRENCES = 100;

/**
 * One scope's events plus their revs, or the fact that the scope failed.
 *
 * Both reads are inside one `try`: a metadata read that fails on its own is
 * already degraded VISIBLY (null revs → `expected_rev_required`), but a row
 * read that fails means we have nothing, and reporting that scope as consulted
 * would be the lie described in the header.
 */
async function loadScope(wire) {
  const scopeKey = scopeKeyForCalendar(wire);
  const rows = await listCalendarRows(scopeKey);
  const revs = await revsForCalendar(scopeKey);
  const events = rows
    .map(eventOf)
    .filter(Boolean)
    // The app's own read rule (calendarStore.eventInScope): a team scope pins
    // `calendar`, and reminders are clamped to MAX_REMINDERS (card 25e3e5a2).
    .map((e) => eventInScope(e, wire));
  return { wire, events, revs };
}

/** Load every requested scope, splitting the failures out honestly. */
async function loadScopes(requested) {
  const { scopes, consulted, unavailable } = resolveScopes(requested);
  const loaded = [];
  const failed = [];
  for (const wire of scopes) {
    try {
      // eslint-disable-next-line no-await-in-loop
      loaded.push(await loadScope(wire));
    } catch {
      failed.push(wire);
    }
  }
  return {
    loaded,
    scopesConsulted: consulted.filter((s) => !failed.includes(s)),
    scopesUnavailable: [...new Set([...unavailable, ...failed])],
  };
}

/** Index a scope's events by uid, so an override can find its master. */
function indexByUid(events) {
  return new Map(events.map((e) => [e.uid, e]));
}

/**
 * One expanded instance → the wire row, with the identity and CAS target §B3
 * prescribes. Three kinds, and the rev differs for each — which is exactly why
 * the caller resolves it rather than `projectOccurrence` guessing.
 */
function rowFor(instance, { wire, byUid, revs }) {
  // (1) A materialized override record: it passed through expansion as itself.
  if (instance.override_of?.master_uid && instance.override_of.occurrence_date) {
    const master = byUid.get(instance.override_of.master_uid) || null;
    return projectOccurrence(instance, {
      scope: wire,
      // The OVERRIDE record's own rev — that is what a further edit CASes on.
      rev: revs.get(instance.uid) ?? null,
      overrideRecordId: instance.uid,
      appointmentId: instance.override_of.master_uid,
      occurrenceDate: instance.override_of.occurrence_date,
      recurring: true,
      recurrence: master?.recurrence || null,
      record: instance,
    });
  }
  // (2) A generated occurrence of a series: no record of its own yet, so the
  // MASTER's rev is the guard an edit is checked against.
  if (instance.is_occurrence) {
    return projectOccurrence(instance, {
      scope: wire,
      rev: revs.get(instance.uid) ?? null,
      overrideRecordId: null,
      appointmentId: instance.uid,
      occurrenceDate: instance.occurrence_date,
      recurring: true,
      recurrence: instance.recurrence || null,
      record: byUid.get(instance.uid) || instance,
    });
  }
  // (3) A plain single event — its own master.
  return projectOccurrence(instance, {
    scope: wire,
    rev: revs.get(instance.uid) ?? null,
    overrideRecordId: null,
    appointmentId: instance.uid,
    occurrenceDate: null,
    recurring: false,
    recurrence: null,
    record: instance,
  });
}

/**
 * `expandEvents` windows only the instances it GENERATES: a single event and a
 * materialized override pass through untouched, whatever their date
 * (`calendarRecurrence.js`'s `if (!event.recurrence) { out.push(event) }`). The
 * app gets away with it because `CalendarView` re-filters per grid cell through
 * `eventTouchesDay` / `eventsInRange`; a tool that returns the array as-is
 * would answer "what is on Tuesday" with the user's entire calendar.
 *
 * So the window is applied here too, with the app's own `eventRange` — which is
 * also where the two end conventions live (timed `end` exclusive, all-day
 * `end_date` inclusive and therefore +1 day).
 */
function intersectsWindow(instance, windowStart, windowEnd) {
  const { start, end } = eventRange(instance);
  return start < windowEnd && end > windowStart;
}

/** Chronological ordering key: all-day rows sort at local midnight of their day. */
function sortKeyOf(row) {
  if (row.allDay) {
    const d = fromDateKey(row.startDate);
    return d ? d.getTime() : 0;
  }
  return row.start ? new Date(row.start).getTime() : 0;
}

export async function appointmentsList({ scope, from, to, limit, cursor }) {
  const badFrom = checkDateKey(from, 'from');
  if (badFrom) return badFrom;
  const badTo = checkDateKey(to, 'to');
  if (badTo) return badTo;

  const windowStart = startOfDay(fromDateKey(String(from)));
  const windowEnd = startOfDay(fromDateKey(String(to)));
  if (windowEnd <= windowStart) {
    return mcpError('invalid_argument', 'to must be after from; to is EXCLUSIVE.', { field: 'to' });
  }
  const spanDays = Math.round((windowEnd - windowStart) / 86_400_000);
  if (spanDays > MAX_WINDOW_DAYS) {
    return mcpError(
      'invalid_argument',
      `That window is ${spanDays} days. Expansion is done on the user's own machine, so it is capped at ${MAX_WINDOW_DAYS} days — ask for a shorter window and page.`,
      { field: 'to', max: MAX_WINDOW_DAYS }
    );
  }

  if (limit !== undefined && (!Number.isInteger(limit) || limit < 1 || limit > MAX_LIMIT)) {
    return mcpError('invalid_argument', `limit must be a whole number from 1 to ${MAX_LIMIT}.`, {
      field: 'limit',
    });
  }
  const pageSize = limit ?? DEFAULT_LIMIT;

  const start = Number.parseInt(cursor ?? '0', 10);
  const offset = Number.isFinite(start) && start >= 0 ? start : 0;

  const { loaded, scopesConsulted, scopesUnavailable } = await loadScopes(scope);

  const rows = [];
  for (const { wire, events, revs } of loaded) {
    const byUid = indexByUid(events);
    for (const instance of expandEvents(events, windowStart, windowEnd)) {
      if (!intersectsWindow(instance, windowStart, windowEnd)) continue;
      rows.push(rowFor(instance, { wire, byUid, revs }));
    }
  }
  // `expandEvents` promises no order across masters, so this is the ordering,
  // with the id as a stable tie-break (two appointments genuinely can start at
  // the same instant).
  rows.sort(
    (a, b) =>
      sortKeyOf(a) - sortKeyOf(b) ||
      String(a.appointmentId).localeCompare(String(b.appointmentId)) ||
      String(a.occurrenceDate ?? '').localeCompare(String(b.occurrenceDate ?? ''))
  );

  const page = rows.slice(offset, offset + pageSize);
  const truncated = offset + page.length < rows.length;
  return {
    appointments: page,
    count: rows.length,
    from: String(from),
    to: String(to),
    timezone: hostTimezone(),
    truncated,
    ...(truncated ? { nextCursor: String(offset + page.length) } : {}),
    scopesConsulted,
    scopesUnavailable,
  };
}

export async function appointmentRead({
  appointment_id: appointmentId,
  scope,
  occurrence_date: occurrenceDate,
  occurrences_from: occurrencesFrom,
  occurrences_limit: occurrencesLimit,
}) {
  if (occurrenceDate !== undefined && occurrenceDate !== null) {
    const bad = checkDateKey(occurrenceDate, 'occurrence_date');
    if (bad) return bad;
  }
  if (occurrencesFrom !== undefined && occurrencesFrom !== null) {
    const bad = checkDateKey(occurrencesFrom, 'occurrences_from');
    if (bad) return bad;
  }
  if (
    occurrencesLimit !== undefined &&
    (!Number.isInteger(occurrencesLimit) || occurrencesLimit < 1 || occurrencesLimit > MAX_OCCURRENCES)
  ) {
    return mcpError(
      'invalid_argument',
      `occurrences_limit must be a whole number from 1 to ${MAX_OCCURRENCES}.`,
      { field: 'occurrences_limit' }
    );
  }

  const { loaded, scopesConsulted, scopesUnavailable } = await loadScopes(scope);
  const id = String(appointmentId);

  let found = null;
  for (const entry of loaded) {
    const master = entry.events.find((e) => e.uid === id);
    if (master) {
      found = { entry, master };
      break;
    }
  }
  if (!found) {
    return mcpError('not_found', 'No appointment with that id in the scopes consulted.', {
      scopesConsulted,
      scopesUnavailable,
    });
  }

  const { entry, master } = found;
  const byUid = indexByUid(entry.events);
  const overrides = entry.events.filter((e) => e.override_of?.master_uid === master.uid);

  // One occurrence of a series, by (appointment_id, occurrence_date).
  if (occurrenceDate) {
    if (!master.recurrence) {
      return mcpError(
        'invalid_parent',
        'That appointment does not repeat, so it has no occurrences. Read it without occurrence_date.',
        { appointmentId: master.uid }
      );
    }
    const day = fromDateKey(String(occurrenceDate));
    const instances = expandEvents([master, ...overrides], day, addDays(day, 1));
    const hit = instances.find(
      (i) =>
        (i.occurrence_date || i.override_of?.occurrence_date) === String(occurrenceDate)
    );
    if (!hit) {
      return mcpError(
        'not_found',
        'That series has no occurrence on that date — the rule does not generate one, or it has been cancelled.',
        { appointmentId: master.uid, occurrenceDate: String(occurrenceDate) }
      );
    }
    return {
      appointment: rowFor(hit, { wire: entry.wire, byUid, revs: entry.revs }),
      timezone: hostTimezone(),
      scopesConsulted,
      scopesUnavailable,
    };
  }

  // Without an `occurrence_date` the caller asked about the SERIES, so this is
  // the MASTER's own stored times and its own rule — not an instance, and not
  // `rowFor`, whose three branches are about instances.
  const head = projectOccurrence(master, {
    scope: entry.wire,
    rev: entry.revs.get(master.uid) ?? null,
    overrideRecordId: null,
    appointmentId: master.uid,
    occurrenceDate: null,
    recurring: !!master.recurrence,
    recurrence: master.recurrence || null,
    record: master,
  });

  let occurrences = [];
  if (master.recurrence) {
    const previewFrom = occurrencesFrom ? fromDateKey(String(occurrencesFrom)) : startOfDay(new Date());
    const limit = occurrencesLimit ?? DEFAULT_OCCURRENCES;
    const previewEnd = addDays(previewFrom, MAX_WINDOW_DAYS);
    occurrences = expandEvents([master, ...overrides], previewFrom, previewEnd)
      .filter((i) => intersectsWindow(i, startOfDay(previewFrom), previewEnd))
      .map((i) => rowFor(i, { wire: entry.wire, byUid, revs: entry.revs }))
      .sort((a, b) => sortKeyOf(a) - sortKeyOf(b))
      .slice(0, limit);
  }

  return {
    appointment: {
      ...head,
      // `exdates` and `override_of` are absent from a list row on purpose; here
      // they are the point — an agent cannot reason about a series without
      // knowing which days were cancelled.
      exdates: Array.isArray(master.exdates) ? master.exdates : [],
      overrideRecordIds: overrides.map((o) => o.uid),
    },
    occurrences,
    occurrencesFrom: occurrencesFrom ? String(occurrencesFrom) : toDateKey(startOfDay(new Date())),
    timezone: hostTimezone(),
    scopesConsulted,
    scopesUnavailable,
  };
}
